"use client";

import { useEffect, useRef } from "react";
import { piEvents } from "@/lib/analytics";

const VISIBILITY_RATIO = 0.5;
/** Ladder of thresholds so tall sections still emit callbacks while scrolling past. */
const THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5];

/** Fires `pi_landing_section_viewed` once when the section scrolls into view. */
export function useSectionViewEvent({
  sectionId,
  sectionName,
  sectionPosition,
  enabled = true,
}: {
  sectionId: string;
  sectionName: string;
  sectionPosition: number;
  enabled?: boolean;
}) {
  const hasTracked = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const node = document.getElementById(sectionId);
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || hasTracked.current) return;

        // A section taller than the viewport can never reach the ratio, so half a
        // screenful of it also counts as exposure.
        const isExposed =
          entry.intersectionRatio >= VISIBILITY_RATIO ||
          entry.intersectionRect.height >= window.innerHeight * VISIBILITY_RATIO;

        if (!isExposed) return;

        hasTracked.current = true;
        piEvents.sectionViewed({ sectionName, sectionPosition });
        observer.disconnect();
      },
      { threshold: THRESHOLDS },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [sectionId, sectionName, sectionPosition, enabled]);
}
